import { Box, CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';
import {
  SearchInputWrapper,
  SearchButton,
  SearchWrapper,
  AcademicYearWrapper
} from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import { academicYears } from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import DeclarationSetupTable from './DeclarationTables/DeclarationSetupTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  deleteDeclarationForGrade,
  fetchDeclarationSetupData
} from '../../apis/declarationSetup';
import { DropdownOption, SchoolDeclarationType } from './types';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import AddDeclarationForGradeModal from './components/AddDeclarationForGradeModal';
import EditDeclarationForGradeModal from './components/EditDeclarationForGradeModal';
import { isEmpty } from 'lodash';
import { ButtonContainer } from '../Staff/styled';
import { Title } from '../common/styled';

const DeclarationSetupPage = () => {
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>('');
  const [declarationSetupData, setDeclarationSetupData] = useState<
    SchoolDeclarationType[]
  >([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [selectedDeclaration, setSelectedDeclaration] =
    useState<SchoolDeclarationType | null>(null);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const { gradeOptions } = useSchoolClassesConfigHook();

  const existingGrades = useMemo(
    () => new Set(declarationSetupData.map((data) => data.grade)),
    [declarationSetupData]
  );

  const filteredGrades = useMemo(
    () =>
      gradeOptions.filter(
        (option: DropdownOption) =>
          !existingGrades.has(option.value) &&
          !String(option.value).includes('INCOMING')
      ),
    [gradeOptions, existingGrades]
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastOpen(true);
    setToastMessage(message);
    setToastSeverity(severity);
  };

  const fetchDeclarationData = async () => {
    try {
      const response = await fetchDeclarationSetupData(
        loggedInUser.school,
        selectedAcademicYear
      );
      setDeclarationSetupData(response as any);
      showToast(
        'Successfully fetched declaration data',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message || 'Error fetching declaration data',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSearch = async () => {
    setDeclarationSetupData([]);
    setIsSearching(true);
    await fetchDeclarationData();
    setIsSearching(false);
    setHasSearched(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleDeleteClick = (selectedDeclaration: SchoolDeclarationType) => {
    setIsDeleting(false);
    setSelectedDeclaration(selectedDeclaration);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleting(false);
    setDeleteModalOpen(false);
    setSelectedDeclaration(null);
  };

  const deleteGradeDeclarationConfirm = async () => {
    if (isEmpty(selectedDeclaration) || !selectedDeclaration?.id) {
      showToast(
        'No declaration selected for deletion.',
        SnackbarSeverityType.error
      );
      handleDeleteCancel();
      return;
    }
    setIsDeleting(true);
    try {
      await deleteDeclarationForGrade(selectedDeclaration?.id);
      await fetchDeclarationData();
      showToast(
        `Declaration for grade ${selectedDeclaration.grade} deleted successfully`,
        SnackbarSeverityType.success
      );
    } catch (error) {
      showToast(`Error in deleting declaration`, SnackbarSeverityType.error);
    } finally {
      setIsDeleting(false);
      handleDeleteCancel();
    }
  };

  const handleEditOpenModal = (selectedDeclaration: SchoolDeclarationType) => {
    setSelectedDeclaration(selectedDeclaration);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedDeclaration(null);
  };

  const handleApiSuccessAndFailure = (isSuccess: boolean, message: string) => {
    setIsAddModalOpen(false);
    showToast(
      message,
      isSuccess ? SnackbarSeverityType.success : SnackbarSeverityType.error
    );
  };

  const isSearchButtonDisabled = isSearching || !selectedAcademicYear;

  return (
    <Box>
      <Title>
        Declarations
      </Title>
      <SearchWrapper>
        <SearchInputWrapper>
          <AcademicYearWrapper>
            <DropdownSelect
              label='Academic Year'
              name='academicYear'
              options={academicYears.filter(
                (option) => option.value !== '2024-2025'
              )}
              value={selectedAcademicYear}
              onChange={(event) => {
                setHasSearched(false);
                setSelectedAcademicYear(event.target.value as string);
              }}
              placeholder='Select Academic Year'
              hasTopLabel
            />
          </AcademicYearWrapper>
          <ButtonContainer>
            <SearchButton
              onClick={handleSearch}
              disabled={isSearchButtonDisabled}
            >
              {isSearching ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Search'
              )}
            </SearchButton>
          </ButtonContainer>
        </SearchInputWrapper>
      </SearchWrapper>

      {hasSearched && !isSearching && (
        <DeclarationSetupTable
          declarationSetupData={declarationSetupData}
          onEdit={handleEditOpenModal}
          onDelete={handleDeleteClick}
          onAddDeclarationForGrade={setIsAddModalOpen}
        />
      )}

      <AddDeclarationForGradeModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        grades={filteredGrades}
        school={loggedInUser.school}
        academicYear={selectedAcademicYear}
        fetchData={fetchDeclarationData}
        onApiSuccessAndFailure={handleApiSuccessAndFailure}
      />
      <EditDeclarationForGradeModal
        declarationSetupData={declarationSetupData}
        setDeclarationSetupData={setDeclarationSetupData}
        open={isEditModalOpen}
        onClose={handleEditModalClose}
        selectedDeclaration={selectedDeclaration}
        fetchData={fetchDeclarationData}
        showToast={showToast}
        gradeOptions={gradeOptions}
        onApiSuccessAndFailure={handleApiSuccessAndFailure}
      />

      <ConfirmationDialog
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        message={`Are you sure you want to delete declaration for grade ${selectedDeclaration?.grade}`}
        onConfirm={deleteGradeDeclarationConfirm}
        disable={isDeleting}
      />

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default DeclarationSetupPage;
